"use client";

import { useState, useRef, useEffect } from "react";

export interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  placeholder: string;
  value: string;
  onChange: (val: string) => void;
  id?: string;
  icon?: React.ElementType;
  className?: string;
  dropdownClassName?: string;
  iconClassName?: string;
  openArrow?: boolean;
  followDir?: boolean;
}

const CustomSelect = ({
  options,
  placeholder,
  value,
  onChange,
  id,
  icon: Icon,
  className = "",
  dropdownClassName = "",
  iconClassName = "",
  openArrow = false,
  followDir = false,
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedLabel =
    options?.find((opt) => opt.value === value)?.label || placeholder;

  return (
    <div className="relative w-full" ref={containerRef} id={id}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`cursor-pointer bg-white py-1 text-primary flex justify-between items-center gap-2 min-w-[160px] ${className}`}
      >
        <div
          className={`flex ${
            followDir ? "flex-row-reverse" : ""
          } items-center gap-2`}
        >
          <span
            className={`${value ? "text-inherit" : "text-text-disabled"} truncate max-w-[200px] md:max-w-[120px]`}
            title={selectedLabel}
          >
            {selectedLabel}
          </span>
          {Icon && (
            <Icon className={`w-5 h-5 ${iconClassName || "text-gray-400"}`} />
          )}
        </div>

        {openArrow && (
          <svg
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`ml-2 transform transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <path
              d="M1 1L5 5L9 1"
              stroke="#9CA3AF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {isOpen && (
        <div
          className={`absolute top-full rtl:right-0 ltr:left-0 mt-1 bg-white rounded-lg shadow-lg z-[150] overflow-hidden border border-gray-100 ${dropdownClassName}`}
        >
          <div className="max-h-[200px] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {options?.map((option) => (
              <div
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-[#E6E6E6] cursor-pointer transition-colors duration-150 truncate"
                title={option.label}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
